import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useAuth } from "@/context/AuthContext";
import api from "@/api/client";
import {
  Fuel, ShieldCheck, Truck, Zap, MapPin, IndianRupee,
  ArrowRight, Wallet, MailCheck, Clock, RotateCw, UserPlus,
} from "lucide-react";

/**
 * Customer self-signup with mandatory OTP verification.
 *
 * Flow:
 *   Step 1 (form)  → POST /auth/register  → returns { requires_verification, user_id, masked_email, masked_phone, ttl_seconds }
 *   Step 2 (otp)   → POST /auth/register/verify → returns { verified, activated, user? }
 *   Resend         → POST /auth/register/resend
 *
 * Theme is loaded from the CMS (same cache key as Login.jsx) so branding stays
 * consistent between the two auth screens without a re-fetch flash.
 */
const CACHE_KEY = "fuelexpress_theme_cache_v1";

export default function Register() {
  const nav = useNavigate();
  const { setUser } = useAuth();

  // Hydrate from the localStorage theme cache (populated by Login.jsx / landing page)
  const cachedTheme = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "null") || {}; }
    catch { return {}; }
  }, []);

  const [brand, setBrand] = useState({
    name: cachedTheme?.brand?.name || "FUELEXPRESS",
    logo_url: cachedTheme?.brand?.logo_url || "",
    customer_login_tagline: cachedTheme?.brand?.customer_login_tagline || "Doorstep diesel delivery",
    customer_login_enabled: cachedTheme?.brand?.customer_login_enabled !== false,
  });
  const [customerTheme, setCustomerTheme] = useState({
    gradient_from: "#EA580C", gradient_to: "#F59E0B", accent: "#F97316",
    card_bg: "#FFFFFF", panel_bg: "#FFF9F5", text_on_gradient: "#FFFFFF",
    ...(cachedTheme.customer_theme || {}),
  });

  useEffect(() => {
    api.get("/cms/landing")
      .then((r) => {
        const c = r.data?.content || r.data || {};
        setBrand({
          name: c?.brand?.name || "FUELEXPRESS",
          logo_url: c?.brand?.logo_url || "",
          customer_login_tagline: c?.brand?.customer_login_tagline || "Doorstep diesel delivery",
          customer_login_enabled: c?.brand?.customer_login_enabled !== false,
        });
        if (c?.customer_theme) setCustomerTheme((t) => ({ ...t, ...c.customer_theme }));
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({
            customer_theme: c?.customer_theme || null,
            staff_theme: c?.staff_theme || null,
            brand: c?.brand || null,
            _cached_at: Date.now(),
          }));
        } catch { /* localStorage unavailable in private mode */ }
      })
      .catch(() => {});
  }, []);

  // ─────────── Signup form state ───────────
  const [step, setStep] = useState("form"); // "form" | "otp" | "done"
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // ─────────── OTP state ───────────
  const [otp, setOtp] = useState("");
  const [otpMeta, setOtpMeta] = useState({
    masked_email: "", masked_phone: "", ttl_seconds: 600,
  });
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [ttlRemaining, setTtlRemaining] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const t = setInterval(() => setResendCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [resendCooldown]);

  useEffect(() => {
    if (ttlRemaining <= 0) return undefined;
    const t = setInterval(() => setTtlRemaining((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [ttlRemaining]);

  const customerSignupDisabled = brand.customer_login_enabled === false;

  const submitForm = async (e) => {
    e.preventDefault();
    if (customerSignupDisabled) {
      toast.error("Customer sign-up is temporarily unavailable.");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, role: "customer" });
      if (data?.requires_verification) {
        setOtpMeta({
          masked_email: data.masked_email || "",
          masked_phone: data.masked_phone || "",
          ttl_seconds: data.ttl_seconds || 600,
        });
        setTtlRemaining(data.ttl_seconds || 600);
        setResendCooldown(data.resend_cooldown_seconds || 60);
        setStep("otp");
        toast.success("Verification code sent — check your email and phone.");
      } else {
        // Should not happen in the new flow, but be defensive.
        toast.success("Account created");
        nav("/login");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e?.preventDefault();
    if (otp.length !== 4) { toast.error("Enter the 4-digit code"); return; }
    setOtpLoading(true);
    try {
      const { data } = await api.post("/auth/register/verify", {
        email: form.email.trim().toLowerCase(),
        otp,
      });
      if (data?.activated && data?.user) {
        setUser(data.user);
        toast.success(`Welcome ${data.user.name}!`);
        nav("/portal/dashboard");
        return;
      }
      // Verified but not activated — admin approval required.
      setStep("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed");
    } finally {
      setOtpLoading(false);
    }
  };

  const resendOtp = async () => {
    if (resendCooldown > 0 || otpLoading) return;
    setOtpLoading(true);
    try {
      const { data } = await api.post("/auth/register/resend", {
        email: form.email.trim().toLowerCase(),
      });
      setResendCooldown(data?.resend_cooldown_seconds || 60);
      setTtlRemaining(data?.ttl_seconds || 600);
      setOtp("");
      toast.success("A fresh code has been sent.");
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not resend code";
      toast.error(detail);
      if (err.response?.status === 429) setResendCooldown(60);
    } finally {
      setOtpLoading(false);
    }
  };

  const T = customerTheme;

  return (
    <div
      className="relative grid min-h-screen grid-cols-1 overflow-hidden md:grid-cols-[1.1fr_1fr]"
      style={{ backgroundColor: T.panel_bg }}
      data-testid="register-page"
    >
      {/* Ambient background flourishes */}
      <div aria-hidden className="pointer-events-none absolute -left-40 -top-40 h-[32rem] w-[32rem] rounded-full opacity-30 blur-3xl" style={{ backgroundColor: T.gradient_from }} />
      <div aria-hidden className="pointer-events-none absolute -right-24 top-1/3 h-96 w-96 rounded-full opacity-40 blur-3xl" style={{ backgroundColor: T.gradient_to }} />

      {/* ─────────── LEFT: Story panel ─────────── */}
      <div
        className="relative hidden md:flex md:flex-col md:justify-between md:overflow-hidden md:p-10 lg:p-14"
        style={{
          background: `linear-gradient(135deg, ${T.gradient_from}, ${T.gradient_to})`,
          color: T.text_on_gradient,
        }}
      >
        <Link to="/" className="relative z-10 flex items-center gap-3">
          {brand.logo_url ? (
            <img
              src={brand.logo_url}
              alt={brand.name}
              className="h-12 w-12 rounded-xl bg-white object-contain p-1 shadow-lg"
              data-testid="register-brand-logo"
            />
          ) : (
            <div
              className="grid h-12 w-12 place-items-center rounded-xl shadow-lg"
              style={{ backgroundColor: "#FFFFFF", color: T.accent }}
            >
              <Fuel size={22} strokeWidth={2.5} />
            </div>
          )}
          <div>
            <div className="font-display text-xl font-black leading-none tracking-tight" data-testid="register-brand-name">{brand.name}</div>
            <div
              className="mt-1 text-[10px] font-bold uppercase tracking-[0.25em]"
              style={{ color: "rgba(255,255,255,0.75)" }}
            >
              {brand.customer_login_tagline}
            </div>
          </div>
        </Link>

        <div className="relative z-10 max-w-lg">
          <Link
            to="/"
            className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest opacity-80 hover:opacity-100 hover:underline"
            style={{ color: "rgba(255,255,255,0.85)" }}
            data-testid="link-back-home-panel"
          >
            <ArrowRight size={12} className="rotate-180" /> Back to home
          </Link>
          <div
            className="mb-5 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] backdrop-blur"
            style={{ borderColor: "rgba(255,255,255,0.25)", background: "rgba(255,255,255,0.10)", color: T.text_on_gradient }}
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-75" style={{ backgroundColor: T.text_on_gradient }} />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full" style={{ backgroundColor: T.text_on_gradient }} />
            </span>
            60-second onboarding · Trusted by 500+ businesses
          </div>

          <h1 className="font-display text-5xl font-black leading-[1.02] tracking-tight lg:text-6xl">
            Book your first <br />delivery in <span className="underline decoration-white/40 decoration-4 underline-offset-8">60 seconds.</span>
          </h1>

          <p className="mt-5 text-base" style={{ color: "rgba(255,255,255,0.85)" }}>
            No station queues. No manual paperwork. Just diesel, delivered to your site — with live GPS tracking and instant GST invoices.
          </p>

          <div className="mt-8 grid grid-cols-2 gap-3">
            {[
              { i: Truck, t: "Doorstep 60-min SLA" },
              { i: MapPin, t: "Live GPS tracking" },
              { i: Wallet, t: "Wallet + Razorpay" },
              { i: ShieldCheck, t: "PESO approved bowsers" },
            ].map(({ i: Icon, t }) => (
              <div
                key={t}
                className="flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-sm font-medium backdrop-blur"
                style={{ borderColor: "rgba(255,255,255,0.20)", background: "rgba(255,255,255,0.10)" }}
              >
                <span
                  className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg"
                  style={{ backgroundColor: "rgba(255,255,255,0.25)", color: T.text_on_gradient }}
                >
                  <Icon size={14} strokeWidth={2.5} />
                </span>
                {t}
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 grid grid-cols-3 gap-2 border-t pt-5" style={{ borderColor: "rgba(255,255,255,0.15)" }}>
          {[
            { v: "50K+", l: "Deliveries" },
            { v: "60 min", l: "Avg. arrival" },
            { v: "4.8★", l: "Rating" },
          ].map((s) => (
            <div key={s.l}>
              <div className="font-display text-2xl font-black">{s.v}</div>
              <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.7)" }}>
                {s.l}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─────────── RIGHT: Sign-up card ─────────── */}
      <div className="relative z-10 flex flex-col items-center justify-center px-6 py-12">
        <div className="mb-4 w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors"
            style={{ borderColor: `${T.accent}33`, color: T.accent, background: "#fff" }}
            data-testid="link-back-home"
          >
            <ArrowRight size={11} className="rotate-180" />
            Back to home
          </Link>
        </div>

        <Card
          className="w-full max-w-md shadow-2xl"
          style={{
            backgroundColor: T.card_bg,
            boxShadow: `0 25px 50px -12px ${T.accent}20`,
            borderColor: `${T.accent}22`,
          }}
          data-testid="register-card"
        >
          {customerSignupDisabled ? (
            <CardContent className="p-8 text-center">
              <div
                className="mx-auto grid h-12 w-12 place-items-center rounded-full"
                style={{ background: `${T.accent}22`, color: T.accent }}
              >
                <ShieldCheck size={22} />
              </div>
              <div className="mt-3 font-display text-lg font-bold text-slate-900">
                Customer sign-up is temporarily unavailable
              </div>
              <p className="mt-1 text-sm text-slate-600">
                We&apos;re performing scheduled maintenance. Please check back shortly, or reach out to our support team.
              </p>
              <Link
                to="/#contact"
                className="mt-4 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white"
                style={{ background: T.accent }}
                data-testid="disabled-contact-support"
              >
                Contact support <ArrowRight size={12} />
              </Link>
            </CardContent>
          ) : step === "form" ? (
            <>
              <CardHeader className="space-y-2 pb-2">
                <div className="flex items-center justify-between">
                  <div
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest"
                    style={{ backgroundColor: `${T.accent}1F`, color: T.accent }}
                  >
                    <UserPlus size={11} /> New account
                  </div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Step 1 of 2 · Details
                  </div>
                </div>
                <CardTitle className="font-display text-3xl">Create your account</CardTitle>
                <CardDescription className="text-slate-500">
                  We&apos;ll send a 4-digit code to your email and phone to verify it&apos;s really you.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submitForm} className="mt-4 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Company / Full name</Label>
                    <Input id="name" required value={form.name} onChange={set("name")} placeholder="Acme Industries Pvt Ltd" data-testid="input-name" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" data-testid="input-email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone (with country code)</Label>
                    <Input id="phone" required value={form.phone} onChange={set("phone")} placeholder="+91 90000 00000" data-testid="input-phone" />
                    <div className="text-[11px] text-slate-500">We&apos;ll SMS your 4-digit code here.</div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" type="password" required minLength={6} value={form.password} onChange={set("password")} placeholder="Min 6 characters" data-testid="input-password" />
                  </div>
                  <Button
                    type="submit"
                    className="w-full font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: T.accent }}
                    disabled={loading}
                    data-testid="btn-register"
                  >
                    <MailCheck size={16} className="mr-2" />
                    {loading ? "Sending verification code..." : "Continue → verify email & phone"}
                  </Button>
                </form>
                <div className="mt-5 text-center text-sm text-slate-500">
                  Already have an account?{" "}
                  <Link to="/login" className="font-semibold hover:underline" style={{ color: T.accent }} data-testid="link-login">
                    Sign in
                  </Link>
                </div>
                <div className="mt-6 flex items-center justify-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                  <ShieldCheck size={11} />
                  Protected by JWT · HTTPS · Rate limited
                </div>
              </CardContent>
            </>
          ) : step === "otp" ? (
            <>
              <CardHeader className="space-y-2 pb-2">
                <div className="flex items-center justify-between">
                  <div
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest"
                    style={{ backgroundColor: `${T.accent}1F`, color: T.accent }}
                  >
                    <Zap size={11} /> Verify
                  </div>
                  <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">
                    Step 2 of 2 · OTP
                  </div>
                </div>
                <CardTitle className="font-display text-3xl">Enter your code</CardTitle>
                <CardDescription className="text-slate-500">
                  We sent a 4-digit code to <b>{otpMeta.masked_email}</b>
                  {otpMeta.masked_phone && <> and <b>{otpMeta.masked_phone}</b></>}.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={submitOtp} className="mt-4 space-y-5">
                  <div className="rounded-xl border p-3 text-xs" style={{ borderColor: `${T.accent}55`, background: `${T.accent}0F` }}>
                    <div className="mb-1 flex items-center gap-2 font-semibold">
                      <ShieldCheck size={14} style={{ color: T.accent }} />
                      Code expires in {Math.floor(ttlRemaining / 60)}:{String(ttlRemaining % 60).padStart(2, "0")}
                    </div>
                    <div className="opacity-70">Wrong email or phone? <button type="button" onClick={() => setStep("form")} className="underline hover:opacity-100" data-testid="btn-change-details">Edit details</button></div>
                  </div>

                  <div className="space-y-2">
                    <Label>4-digit code</Label>
                    <div className="flex justify-center">
                      <InputOTP maxLength={4} value={otp} onChange={setOtp} data-testid="input-register-otp" autoFocus>
                        <InputOTPGroup>
                          {[0, 1, 2, 3].map((i) => <InputOTPSlot key={i} index={i} />)}
                        </InputOTPGroup>
                      </InputOTP>
                    </div>
                    <div className="mt-2 flex items-center justify-center gap-2 text-[11px] text-slate-500">
                      <Clock size={11} />
                      <span>Didn&apos;t get the code?</span>
                      <button
                        type="button"
                        onClick={resendOtp}
                        disabled={resendCooldown > 0 || otpLoading}
                        className="font-semibold underline hover:text-slate-800 disabled:no-underline disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="btn-resend-otp"
                      >
                        {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : (otpLoading ? "Sending..." : "Resend code")}
                      </button>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: T.accent }}
                    disabled={otpLoading || otp.length !== 4}
                    data-testid="btn-verify-register"
                  >
                    <ShieldCheck size={16} className="mr-2" />
                    {otpLoading ? "Verifying..." : "Verify & activate account"}
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-xs">
                    <button
                      type="button"
                      onClick={() => { setStep("form"); setOtp(""); }}
                      className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800"
                      data-testid="btn-back-to-form"
                    >
                      <RotateCw size={11} /> Start over
                    </button>
                  </div>
                </form>
              </CardContent>
            </>
          ) : (
            /* step === "done" — verified but admin approval required */
            <CardContent className="p-8 text-center" data-testid="register-pending-approval">
              <div
                className="mx-auto grid h-14 w-14 place-items-center rounded-full"
                style={{ background: `${T.accent}22`, color: T.accent }}
              >
                <MailCheck size={26} />
              </div>
              <div className="mt-4 font-display text-2xl font-bold text-slate-900">
                Almost there!
              </div>
              <p className="mt-2 text-sm text-slate-600">
                Your email and phone have been verified. An administrator will review and activate your account shortly — you&apos;ll receive a confirmation email once you can sign in.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white"
                style={{ background: T.accent }}
                data-testid="btn-goto-login"
              >
                Back to sign in <ArrowRight size={12} />
              </Link>
            </CardContent>
          )}
        </Card>

        {/* Footer copyright */}
        <div className="mt-6 text-center text-[11px] text-slate-400">
          <div className="flex items-center justify-center gap-3">
            <Link to="/privacy" className="hover:underline">Privacy</Link>
            <span className="opacity-30">·</span>
            <Link to="/terms" className="hover:underline">Terms</Link>
            <span className="opacity-30">·</span>
            <Link to="/#contact" className="hover:underline">Support</Link>
          </div>
          <div className="mt-2 flex items-center justify-center gap-1 opacity-70">
            <IndianRupee size={10} /> © 2026 {brand.name || "FuelExpress"} — All rights reserved
          </div>
        </div>
      </div>
    </div>
  );
}

// Small local hook so we can call setUser without duplicating the AuthContext shape.
// AuthContext already exposes `setUser` indirectly through `register`; we replicate
// just the state update here since the new flow bypasses the register() helper.
function useAuthSetter() {
  const auth = useAuth();
  return {
    setUser: (u) => {
      try { sessionStorage.setItem("ddd_user", JSON.stringify(u)); } catch { /* noop */ }
      // AuthContext bootstraps from sessionStorage on next mount; but for the current
      // mount we also need to force a re-render. The register helper does this, so
      // we route through it by mutating its underlying state via a no-op call.
      if (auth?.register?._setUser) auth.register._setUser(u);
    },
  };
}
